import { FC, Fragment, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  HoldingAvailabilityStatus,
  Icon,
  RecordAvailabilityGeneralStatus,
  RecordAvailabilityIconColor,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { getFormattedDate } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { PropsWithClassName } from '@app/constants';

import './AvailabilityStatus.scss';

interface AvailabilityStatusProps {
  status: HoldingAvailabilityStatus;
  hasCustomMessage?: boolean;
  customStatus?: string;
  hasVolumes?: boolean;
  copiesRemaining?: {
    available: number;
    total: number;
  };
  dueDate?: string;
  StatusTag?: 'strong' | typeof Fragment;
}

const cnBem = createBemBlockBuilder(['availabilityStatus']);

const libraryHoldingStatusMessages = defineMessages({
  [RecordAvailabilityGeneralStatus.available]: {
    id: 'holdingStatus.available',
    defaultMessage: 'Available{colon, select, false {} true {: } other {}}',
  },
  [RecordAvailabilityGeneralStatus.notAvailable]: {
    id: 'holdingStatus.notAvailable',
    defaultMessage: 'Not Available{colon, select, false {} true {: } other {}}',
  },
  [RecordAvailabilityGeneralStatus.error]: {
    id: 'holdingStatus.error',
    defaultMessage:
      'Availability unknown{colon, select, false {} true {: } other {}}',
  },
});

const statusMap = {
  [RecordAvailabilityIconColor.green]: faCheckCircle,
  [RecordAvailabilityIconColor.yellow]: faCalendarAlt,
  [RecordAvailabilityIconColor.red]: faTimesCircle,
  [RecordAvailabilityIconColor.error]: faExclamationTriangle,
};

export const AvailabilityStatus: FC<
  PropsWithClassName<AvailabilityStatusProps>
> = ({
  status: { customDisplayStatus, generalStatus, customIcon },
  hasCustomMessage = false,
  hasVolumes,
  copiesRemaining,
  dueDate,
  StatusTag = Fragment,
}) => {
  const { $t } = useIntl();
  const { locale } = useAppContext();

  const getStatusInfo = ({ info }: { info?: ReactNode } = {}) => (
    <>
      <StatusTag>
        {$t(libraryHoldingStatusMessages[generalStatus], {
          colon: Boolean(info),
        })}
      </StatusTag>
      {info}
    </>
  );

  const getStatusMessage = () => {
    const copiesRemainingMessage =
      copiesRemaining &&
      $t(
        {
          id: 'resultsList.copiesRemaining',
          defaultMessage:
            '{available} of {total} {hasVolumes, select, false {copies} true {volumes} other {}} remaining',
        },
        {
          available: copiesRemaining.available,
          total: copiesRemaining.total,
          hasVolumes,
        }
      );

    if (generalStatus === RecordAvailabilityGeneralStatus.available) {
      return getStatusInfo({ info: copiesRemainingMessage });
    }

    if (generalStatus === RecordAvailabilityGeneralStatus.notAvailable) {
      const info =
        copiesRemainingMessage ||
        (dueDate &&
          $t(sharedMessages.dueDate, {
            date: getFormattedDate({
              date: dueDate,
              locale,
              shouldDisplayTime: false,
            }),
          }));

      return getStatusInfo({ info });
    }

    return getStatusInfo({
      info: $t({
        id: 'resultsList.unableHoldingsStatus',
        defaultMessage: 'Unable to load library holdings status.',
      }),
    });
  };

  const statusMessage = hasCustomMessage
    ? customDisplayStatus
    : getStatusMessage();

  return (
    <Icon
      icon={statusMap[customIcon]}
      className={cnBem(`__icon--${customIcon}`)}
    >
      <span data-auto="availability-status">{statusMessage}</span>
    </Icon>
  );
};
